import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getProjects, createProject, deleteProject, getUsers } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  FolderKanban, 
  Plus, 
  Search, 
  Trash2,
  MoreVertical,
  ArrowRight
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    client_id: '',
  });

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const status = statusFilter === 'all' ? undefined : statusFilter;
      const [projectsRes, clientsRes] = await Promise.all([
        getProjects(status),
        getUsers('approved')
      ]);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      await createProject(newProject);
      toast.success('Project created successfully');
      setCreateDialogOpen(false);
      setNewProject({ name: '', description: '', client_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project and all related data?')) return;
    try {
      await deleteProject(projectId);
      toast.success('Project deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const filteredProjects = projects.filter(project => 
    project.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    project.client_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Projects</h1>
          <p className="text-slate-400">Manage all client projects</p>
        </div>
        
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#4A90D9] hover:bg-[#3A80C9]" data-testid="create-project-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#111827] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Create New Project</DialogTitle>
              <DialogDescription className="text-slate-400">
                Create a new project for a client.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateProject} className="space-y-4">
              <div>
                <Label className="text-slate-300">Project Name *</Label>
                <Input
                  value={newProject.name}
                  onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                  required
                  placeholder="Website Redesign"
                  className="mt-1 bg-[#1E293B] border-slate-700 text-white"
                  data-testid="new-project-name"
                />
              </div>
              <div>
                <Label className="text-slate-300">Client *</Label>
                <Select 
                  value={newProject.client_id} 
                  onValueChange={(value) => setNewProject({ ...newProject, client_id: value })}
                >
                  <SelectTrigger className="mt-1 bg-[#1E293B] border-slate-700 text-white" data-testid="new-project-client">
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-slate-700">
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name} {client.company && `(${client.company})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Description</Label>
                <Textarea
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  placeholder="Project details and scope..."
                  className="mt-1 bg-[#1E293B] border-slate-700 text-white min-h-[100px]"
                  data-testid="new-project-description"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="ghost" 
                  onClick={() => setCreateDialogOpen(false)}
                  className="text-slate-400"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#4A90D9] hover:bg-[#3A80C9]"
                  disabled={!newProject.client_id}
                  data-testid="submit-create-project"
                >
                  Create Project
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="bg-[#111827] border-white/10">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#1E293B] border-slate-700 text-white"
                data-testid="search-projects"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-[#1E293B] border-slate-700 text-white" data-testid="project-status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-slate-700">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="awaiting_approval">Awaiting Approval</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Projects Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : filteredProjects.length === 0 ? (
        <Card className="bg-[#111827] border-white/10">
          <CardContent className="py-16">
            <div className="empty-state">
              <FolderKanban className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No projects found</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="projects-grid">
          {filteredProjects.map((project, index) => (
            <Card 
              key={project.id}
              className="bg-[#111827] border-white/10 card-hover animate-fade-in-up opacity-0"
              style={{ animationDelay: `${0.05 * index}s` }}
              data-testid={`project-card-${project.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-white truncate">{project.name}</h3>
                    <p className="text-sm text-slate-400">{project.client_name}</p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-slate-400 -mr-2">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#1E293B] border-slate-700">
                      <DropdownMenuItem 
                        className="text-red-500 focus:text-red-400 focus:bg-red-500/10"
                        onClick={() => handleDeleteProject(project.id)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {project.description && (
                  <p className="text-sm text-slate-400 line-clamp-2 mb-4">
                    {project.description}
                  </p>
                )}

                <div className="flex items-center gap-2 mb-4">
                  <Badge className={`badge-${project.status}`}>
                    {project.status.replace('_', ' ')}
                  </Badge>
                  <Badge className={`badge-${project.payment_status}`}>
                    {project.payment_status}
                  </Badge>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-white/10">
                  <span className="text-xs text-slate-500">
                    Created {new Date(project.created_at).toLocaleDateString()}
                  </span>
                  <Link to={`/admin/projects/${project.id}`}>
                    <Button variant="ghost" size="sm" className="text-[#4A90D9] hover:text-[#38BDF8]">
                      View <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
